import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { TmdxSwitch } from '@ds';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { Case } from '@/api/apis/case';
import { getQuestionnaireEditControlState } from '@/utils/helpers';
import { CaseDetailsPermissions } from '@/permissions';
import { CaseGeneralFormType } from '../EditCaseForms/EditCaseGeneral/config';

type QuestionaryEditAllowedFieldProps = {
  caseData: Case;
  disabled?: boolean;
};

export const QuestionaryEditAllowedField = ({ caseData, disabled = false }: QuestionaryEditAllowedFieldProps) => {
  const { checkPermission } = useContext(AuthContext);

  const canReadTrialQuestionnaire = checkPermission(CaseDetailsPermissions.TrialQuestionnaireRead);
  const canUpdateTrialQuestionnaire = checkPermission(CaseDetailsPermissions.TrialQuestionnaireUpdate);

  const { control } = useFormContext<CaseGeneralFormType>();

  const [txCenterTrialPrograms, organType] = useWatch<CaseGeneralFormType>({
    control,
    name: ['txCenterTrialPrograms', 'organType'],
  });

  const controlState = useMemo(
    () =>
      getQuestionnaireEditControlState({
        status: caseData.status,
        organType,
        txCenterTrialPrograms,
      }),
    [caseData.status, organType, txCenterTrialPrograms],
  );

  const isVisible = canReadTrialQuestionnaire && controlState.isVisible;
  const isDisabled = disabled || !canUpdateTrialQuestionnaire || controlState.isDisabled;

  if (!isVisible) return null;

  return (
    <FormRow title="Questionary Edit Allowed">
      <Controller
        name="isQuestionaryEditAllowed"
        control={control}
        render={({ field: { value, onChange } }) => {
          const switchValue = controlState.forceEnabled ? true : !!value;

          return (
            <div className="flex items-center gap-3 text-l-m h-10">
              <span>No</span>
              <TmdxSwitch
                isDisabled={isDisabled}
                isSelected={switchValue}
                color="default"
                size="sm"
                onValueChange={onChange}
              />
              <span>Yes</span>
            </div>
          );
        }}
      />
    </FormRow>
  );
};
